import React, { useEffect, useState } from 'react';
import { Button, Space, Typography, message } from 'antd';
import { ServiceField, ServiceCreationState, useServiceCreation } from '../contexts/ServiceCreationContext';
import { useDataProvider } from 'ra-core';
import useGetGeneralSettings from "@src/providers/useGetGeneralSettings";
import { getActiveMembership } from "@src/components/Common/utils";
import SchemaMonacoEditor from '@src/components/Services/SubComponents/SchemaMonacoEditor';
import { validateSchema } from '../../utils/schemaValidation';
import { ExtendedDataProvider } from "@src/types";
import SchemaValidationErrors from '../../utils/SchemaValidationErrors';

const { Title } = Typography;

export const SchemaEditor: React.FC = () => {
  const { state, setJsonSchema, prevStep, nextStep, setServiceId } = useServiceCreation();
  const dataProvider = useDataProvider() as ExtendedDataProvider;
  const [isValidated, setIsValidated] = useState(false);
  const [validationErrors, setValidationErrors] = useState<any>(null);
  
  const generalSettings = useGetGeneralSettings();

  const isSubmitButtonVisible = () => {
    const activeMembership = getActiveMembership()

    const serviceCreatorSettings = generalSettings.find(setting => setting.name == 'ServiceCreator');
    const visibleFromGeneralSettings = serviceCreatorSettings?.config?.allow_service_creation
    const visibleFromTeamPreferences = activeMembership?.team?.preferences?.allow_service_creation

    return visibleFromGeneralSettings && visibleFromTeamPreferences
  }

  useEffect(() => {
    setIsValidated(false);
  }, [state.jsonSchema]);

  useEffect(() => {
    if (!state.jsonSchema || state.selectedProperties.length > 0) {
      const schema = generateJsonSchema(state.selectedProperties, state.basicInfo);
      setJsonSchema(schema);
    }
  }, [state.selectedProperties, state.basicInfo]);

  const handleEditorChange = (value: string | undefined) => {
    setJsonSchema(value || '');
  };

  const handleValidate = async () => {
    const result = await validateSchema(state.jsonSchema, dataProvider);
    if (result.success) {
      message.success(result.message);
      setValidationErrors(null);
    } else {
      setValidationErrors(result.errors);
    }
    setIsValidated(result.success);
  };

  const handleSubmit = async () => {
    try {
      const response = await dataProvider.create('services', { 
        data: JSON.parse(state.jsonSchema),
      });
      message.success('Service created successfully');
      setServiceId(Number(response.data.id));
      nextStep();
    } catch (error) {
      message.error('Failed to create service: ' + (error as Error).message);
    }
  };

  return (
    <div>
      <Title level={4}>JSON Schema Editor</Title>
      {validationErrors && (
        <div style={{ marginTop: '16px' }}>
          <SchemaValidationErrors errors={validationErrors} />
        </div>
      )}
      <SchemaMonacoEditor
        value={state.jsonSchema}
        onChange={handleEditorChange}
      />
      <Space style={{ marginTop: '16px' }}>
        <Button onClick={prevStep}>
          Previous
        </Button>
        <Button onClick={handleValidate}>
          Validate Schema
        </Button>
        {isSubmitButtonVisible() && (
          <Button 
            type="primary" 
            onClick={handleSubmit}
            disabled={!isValidated}
          >
            Create Service
          </Button>
        )}
      </Space>
    </div>
  );
};

function generateJsonSchema(properties: ServiceField[], basicInfo: ServiceCreationState['basicInfo']) {
  const schema = {
    $schema: "https://json-schema.org/draft/2020-12/schema",
    $id: "https://example.com/service.schema.json",
    title: basicInfo.name,
    description: basicInfo.description,
    type: "object",
    additionalProperties: false,
    metadata: {
      cost_per_change: 0,
      monthly_cost: 0
    },
    required: ["name"] as string[],
    properties: {
      name: {
        type: "string"
      }
    } as Record<string, any>
  };

  properties.forEach(prop => {
    const propertyName = prop.name.toLowerCase().replace(/\s+/g, '_');
    
    if (prop.required) {
      schema.required.push(propertyName);
    }
    schema.properties[propertyName] = prop.schema;
  });

  return JSON.stringify(schema, null, 2);
}